import { TextFrame } from '../types';

export interface TextPlayerOptions {
  onTextReceived?: (textFrame: TextFrame) => void;
  onError?: (error: Error) => void;
}

/**
 * Text Player for handling STT (Speech-to-Text) streams
 * Receives JSON text data instead of audio
 */
export class TextPlayer {
  private options: TextPlayerOptions;
  private isInitialized = false;
  private textHistory: TextFrame[] = [];
  private maxHistorySize = 100;

  constructor(options: TextPlayerOptions = {}) {
    this.options = options;
  }

  async initialize(): Promise<void> {
    this.isInitialized = true;
    this.textHistory = [];
  }

  async reset(): Promise<void> {
    this.textHistory = [];
    this.isInitialized = false;
  }

  /**
   * Process a text frame (from STT encoder)
   * @param frameData - Raw binary data containing JSON text frame
   */
  async playTextFrame(frameData: Uint8Array): Promise<void> {
    if (!this.isInitialized) {
      throw new Error('TextPlayer not initialized');
    }

    try {
      // Decode UTF-8 JSON data
      const jsonString = new TextDecoder('utf-8').decode(frameData);
      const textFrame: TextFrame = JSON.parse(jsonString);

      // Validate text frame structure
      if (typeof textFrame.text !== 'string') {
        throw new Error('Invalid text frame: missing text field');
      }

      // Add to history
      this.textHistory.push(textFrame);
      if (this.textHistory.length > this.maxHistorySize) {
        this.textHistory.shift();
      }

      // Notify callback
      if (this.options.onTextReceived) {
        this.options.onTextReceived(textFrame);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (this.options.onError) {
        this.options.onError(err);
      }
      throw err;
    }
  }

  /**
   * Get recent text history
   */
  getTextHistory(): TextFrame[] {
    return [...this.textHistory];
  }

  /**
   * Get the latest text
   */
  getLatestText(): string | null {
    if (this.textHistory.length === 0) return null;
    return this.textHistory[this.textHistory.length - 1].text;
  }

  /**
   * Clear text history
   */
  clearHistory(): void {
    this.textHistory = [];
  }

  dispose(): void {
    this.textHistory = [];
    this.isInitialized = false;
  }

  // Compatibility methods (no-op for text player)
  setVolume(_volume: number): void {
    // No-op: text has no volume
  }

  setMuted(_muted: boolean): void {
    // No-op: text cannot be muted
  }

  async suspend(): Promise<void> {
    // No-op: text playback doesn't need suspension
  }

  async resume(): Promise<void> {
    // No-op: text playback doesn't need resumption
  }
}
